import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AdminService from "../../../api/services/adminService";
import "../Home/Homepage.css";
import "./CurrencyPairManagementPage.css";
import "./AddCurrencyPairPage.css";

const FuturesPairPage = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [coinsList, setCoinsList] = useState([]);
  const [form, setForm] = useState({
    baseCurrency: "",
    quoteCurrency: "",
  });
  const [pairForm, setPairForm] = useState(null);
  const [submitError, setSubmitError] = useState("");
  const [successModal, setSuccessModal] = useState(null);

  const currencies = [...new Set(coinsList.map((c) => c.short_name).filter(Boolean))].sort();

  const fetchCurrencies = useCallback(async () => {
    setLoading(true);
    const res = await AdminService.getCoinsList();
    setLoading(false);
    if (res?.success && Array.isArray(res.data)) {
      setCoinsList(res.data);
    } else {
      setCoinsList([]);
    }
  }, []);

  useEffect(() => {
    fetchCurrencies();
  }, [fetchCurrencies]);

  const updateForm = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const updatePairForm = (key, value) => setPairForm((f) => (f ? { ...f, [key]: value } : null));

  const canProceedStep1 = form.baseCurrency && form.quoteCurrency && form.baseCurrency !== form.quoteCurrency;

  const handleContinueToStep2 = async (e) => {
    e?.preventDefault?.();
    setSubmitError("");
    setFetchLoading(true);
    try {
      const res = await AdminService.getFuturesPairInfo({
        base_currency: form.baseCurrency,
        quote_currency: form.quoteCurrency,
      });

      if (res?.success && res.data) {
        setPairForm({
          base_currency: form.baseCurrency,
          quote_currency: form.quoteCurrency,
          status: res.data.status || "Active",
          price_precision: res.data.price_precision ?? 2,
          quantity_precision: res.data.quantity_precision ?? 3,
          stepSize: res.data.stepSize ?? "",
          tickSize: res.data.tickSize ?? "",
          minNotional: res.data.minNotional ?? "",
          max_leverage: res.data.max_leverage ?? 125,
          maintenanceMarginRate: res.data.maintenanceMarginRate ?? 0.005,
          maker_fee: res.data.maker_fee ?? 0.0002,
          taker_fee: res.data.taker_fee ?? 0.0004,
          buy_price: res.data.buy_price ?? "",
          sell_price: res.data.sell_price ?? "",
        });
        setStep(2);
      } else {
        setSubmitError(res?.message || "Futures symbol not found on Binance.");
      }
    } catch (err) {
      setSubmitError("Failed to fetch futures pair info. Please try again.");
    } finally {
      setFetchLoading(false);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep((s) => s - 1);
      if (step === 2) {
        setPairForm(null);
      }
      setSubmitError("");
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!pairForm) return;
    setSubmitError("");
    setSubmitLoading(true);
    try {
      const payload = {
        base_currency: pairForm.base_currency,
        quote_currency: pairForm.quote_currency,
        status: pairForm.status,
        price_precision: Number(pairForm.price_precision),
        quantity_precision: Number(pairForm.quantity_precision),
        stepSize: Number(pairForm.stepSize),
        tickSize: Number(pairForm.tickSize),
        minNotional: Number(pairForm.minNotional),
        max_leverage: Number(pairForm.max_leverage),
        maintenanceMarginRate: Number(pairForm.maintenanceMarginRate),
        maker_fee: Number(pairForm.maker_fee),
        taker_fee: Number(pairForm.taker_fee),
        buy_price: Number(pairForm.buy_price),
        sell_price: Number(pairForm.sell_price),
      };

      const res = await AdminService.createFuturesPair(payload);
      if (res?.success) {
        setSuccessModal({
          pairName: `${form.baseCurrency}/${form.quoteCurrency}`,
          status: pairForm.status,
          leverage: `${pairForm.max_leverage}x`,
        });
      } else {
        setSubmitError(res?.message || "Failed to create futures pair.");
      }
    } catch (err) {
      setSubmitError("Failed to create futures pair.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleSuccessClose = () => {
    setSuccessModal(null);
    setForm({
      baseCurrency: "",
      quoteCurrency: "",
    });
    setPairForm(null);
    setStep(1);
  };

  if (loading) {
    return (
      <main className="dashboard-main dashboard-subpage">
        <section className="dashboard-hero">
          <div className="dashboard-hero__text">
            <p className="dashboard-hero__breadcrumb">
              <Link to="/dashboard/homepage">Dashboard</Link>
              <span style={{ margin: "0 0.35rem" }}>/</span>
              <span>Futures Trading</span>
              <span style={{ margin: "0 0.35rem" }}>/</span>
              <span>Add Futures Pair</span>
            </p>
            <h1 className="dashboard-hero__title">Add Futures Pair</h1>
          </div>
        </section>
        <div className="addpair-loading">
          <i className="fas fa-spinner fa-spin" /> Loading...
        </div>
      </main>
    );
  }

  return (
    <main className="dashboard-main dashboard-subpage addpair-page">
      <section className="dashboard-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Futures Trading</span>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Add Futures Pair</span>
          </p>
          <h1 className="dashboard-hero__title">Add Futures Pair</h1>
          <p className="dashboard-hero__subtitle">Create a new futures trading pair with leverage and margin settings</p>
        </div>
        <div className="dashboard-hero__icon addpair-hero__icon">
          <i className="fas fa-chart-line" />
        </div>
      </section>

      <div className="dashboard-body addpair-body">
        <div className="addpair-card">
          <div className="cp-stepper addpair-stepper">
            <div className={`cp-stepper-item ${step >= 1 ? "active" : ""}`}>
              <span className="cp-stepper-num">{step > 1 ? <i className="fas fa-check" /> : "1"}</span>
              <span className="cp-stepper-label">Select Currencies</span>
            </div>
            <div className="cp-stepper-line" />
            <div className={`cp-stepper-item ${step >= 2 ? "active" : ""}`}>
              <span className="cp-stepper-num">{step > 2 ? <i className="fas fa-check" /> : "2"}</span>
              <span className="cp-stepper-label">Configure</span>
            </div>
            <div className="cp-stepper-line" />
            <div className={`cp-stepper-item ${step >= 3 ? "active" : ""}`}>
              <span className="cp-stepper-num">3</span>
              <span className="cp-stepper-label">Confirm</span>
            </div>
          </div>

          {submitError && (
            <div className="addpair-alert addpair-alert--error">
              <i className="fas fa-exclamation-circle" />
              <span>{submitError}</span>
            </div>
          )}

          {step === 1 && (
            <form onSubmit={handleContinueToStep2} className="addpair-form">
              <div className="addpair-config-grid">
                <div className="cp-field">
                  <label>Base Currency <span className="required">*</span></label>
                  <div className="cp-input-wrap">
                    <i className="fas fa-gem" />
                    <select
                      value={form.baseCurrency}
                      onChange={(e) => updateForm("baseCurrency", e.target.value)}
                      required
                    >
                      <option value="">Select Base Currency</option>
                      {currencies.filter((c) => c !== form.quoteCurrency).map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="cp-field">
                  <label>Quote Currency <span className="required">*</span></label>
                  <div className="cp-input-wrap">
                    <i className="fas fa-gem" />
                    <select
                      value={form.quoteCurrency}
                      onChange={(e) => updateForm("quoteCurrency", e.target.value)}
                      required
                    >
                      <option value="">Select Quote Currency</option>
                      {currencies.filter((c) => c !== form.baseCurrency).map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div className="addpair-actions">
                <Link to="/dashboard/homepage" className="cp-btn cp-btn--outline">Cancel</Link>
                <button type="submit" className="cp-btn cp-btn--primary" disabled={!canProceedStep1 || fetchLoading}>
                  {fetchLoading ? <><i className="fas fa-spinner fa-spin" /> Fetching...</> : <>Continue <i className="fas fa-arrow-right" /></>}
                </button>
              </div>
            </form>
          )}

          {step === 2 && pairForm && (
            <form onSubmit={(e) => { e.preventDefault(); setStep(3); }} className="addpair-form">
              <p className="cp-step-desc">Edit values if needed. Data prefilled from Binance Futures.</p>
              <div className="addpair-config-grid">
                <div className="cp-field">
                  <label>Buy Price</label>
                  <div className="cp-input-wrap">
                    <input type="number" step="any" min={0} value={pairForm.buy_price} onChange={(e) => updatePairForm("buy_price", e.target.value)} onWheel={(e) => e.target.blur()} required />
                  </div>
                </div>
                <div className="cp-field">
                  <label>Sell Price</label>
                  <div className="cp-input-wrap">
                    <input type="number" step="any" min={0} value={pairForm.sell_price} onChange={(e) => updatePairForm("sell_price", e.target.value)} onWheel={(e) => e.target.blur()} required />
                  </div>
                </div>
                <div className="cp-field">
                  <label>Maker Fee (%)</label>
                  <div className="cp-input-wrap">
                    <input type="number"  value={pairForm.maker_fee} onChange={(e) => updatePairForm("maker_fee", e.target.value)} onWheel={(e) => e.target.blur()} />
                  </div>
                </div>
                <div className="cp-field">
                  <label>Taker Fee (%)</label>
                  <div className="cp-input-wrap">
                    <input type="number" onWheel={(e) => e.target.blur()} value={pairForm.taker_fee} onChange={(e) => updatePairForm("taker_fee", e.target.value)}  />
                  </div>
                </div>
                <div className="cp-field">
                  <label>Quantity Precision</label>
                  <div className="cp-input-wrap">
                    <input type="number" min={0} value={pairForm.quantity_precision} onChange={(e) => updatePairForm("quantity_precision", e.target.value)} onWheel={(e) => e.target.blur()} required />
                  </div>
                </div>
                <div className="cp-field">
                  <label>Price Precision</label>
                  <div className="cp-input-wrap">
                    <input type="number" min={0} value={pairForm.price_precision} onChange={(e) => updatePairForm("price_precision", e.target.value)} onWheel={(e) => e.target.blur()} required />
                  </div>
                </div>
                <div className="cp-field">
                  <label>Step Size</label>
                  <div className="cp-input-wrap">
                    <input type="number" step="any" min={0} value={pairForm.stepSize} onChange={(e) => updatePairForm("stepSize", e.target.value)} onWheel={(e) => e.target.blur()} required />
                  </div>
                </div>
                <div className="cp-field">
                  <label>Tick Size</label>
                  <div className="cp-input-wrap">
                    <input type="number" step="any" min={0} value={pairForm.tickSize} onChange={(e) => updatePairForm("tickSize", e.target.value)} onWheel={(e) => e.target.blur()} required />
                  </div>
                </div>
                <div className="cp-field">
                  <label>Min Notional</label>
                  <div className="cp-input-wrap">
                    <input type="number" step="any" min={0} value={pairForm.minNotional} onChange={(e) => updatePairForm("minNotional", e.target.value)} onWheel={(e) => e.target.blur()} required />
                  </div>
                </div>
                <div className="cp-field">
                  <label>Max Leverage</label>
                  <div className="cp-input-wrap">
                    <input type="number" min={1} max={125} value={pairForm.max_leverage} onChange={(e) => updatePairForm("max_leverage", e.target.value)} onWheel={(e) => e.target.blur()} />
                  </div>
                </div>
                <div className="cp-field">
                  <label>Maintenance Margin Rate</label>
                  <div className="cp-input-wrap">
                    <input type="number" step="0.0001" min={0} value={pairForm.maintenanceMarginRate} onChange={(e) => updatePairForm("maintenanceMarginRate", e.target.value)} onWheel={(e) => e.target.blur()} />
                  </div>
                </div>
                <div className="cp-field">
                  <label>Status</label>
                  <div className="cp-input-wrap">
                    <select value={pairForm.status} onChange={(e) => updatePairForm("status", e.target.value)}>
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="addpair-actions">
                <button type="button" className="cp-btn cp-btn--outline" onClick={handleBack}>
                  <i className="fas fa-arrow-left" /> Back
                </button>
                <button type="submit" className="cp-btn cp-btn--primary">
                  Continue <i className="fas fa-arrow-right" />
                </button>
              </div>
            </form>
          )}

          {step === 3 && pairForm && (
            <div className="addpair-form">
              <p className="cp-step-desc">Confirm and create the futures trading pair.</p>
              <div className="addpair-summary">
                <div className="addpair-summary-row"><span>Pair</span><strong>{form.baseCurrency}/{form.quoteCurrency}</strong></div>
                <div className="addpair-summary-row"><span>Status</span><strong>{pairForm.status}</strong></div>
                <div className="addpair-summary-row"><span>Leverage</span><strong>Max {pairForm.max_leverage}x</strong></div>
                <div className="addpair-summary-row"><span>Buy / Sell Price</span><strong>{pairForm.buy_price || "—"} / {pairForm.sell_price || "—"}</strong></div>
                <div className="addpair-summary-row"><span>Maker / Taker Fee</span><strong>{pairForm.maker_fee}% / {pairForm.taker_fee}%</strong></div>
                <div className="addpair-summary-row"><span>Maintenance Margin Rate</span><strong>{pairForm.maintenanceMarginRate}</strong></div>
              </div>
              <div className="addpair-actions">
                <button type="button" className="cp-btn cp-btn--outline" onClick={handleBack}>
                  <i className="fas fa-arrow-left" /> Back
                </button>
                <button type="button" className="cp-btn cp-btn--primary" onClick={handleSubmit} disabled={submitLoading}>
                  {submitLoading ? <><i className="fas fa-spinner fa-spin" /> Creating...</> : <><i className="fas fa-check" /> Create Futures Pair</>}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {successModal && (
        <div className="addpair-success-overlay" onClick={handleSuccessClose}>
          <div className="addpair-success-modal" onClick={(e) => e.stopPropagation()}>
            <div className="addpair-success-icon"><i className="fas fa-check-circle" /></div>
            <h3>Futures Pair Created Successfully</h3>
            <p>The following futures pair has been created:</p>
            <div className="addpair-success-details">
              <div className="addpair-success-row"><span>Pair</span><strong>{successModal.pairName}</strong></div>
              <div className="addpair-success-row"><span>Status</span><strong>{successModal.status}</strong></div>
              <div className="addpair-success-row"><span>Leverage</span><strong>{successModal.leverage}</strong></div>
            </div>
            <button type="button" className="cp-btn cp-btn--primary" onClick={handleSuccessClose}>Done</button>
          </div>
        </div>
      )}
    </main>
  );
};

export default FuturesPairPage;
